import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { 
  PieChart, 
  BarChart3, 
  Users, 
  TrendingUp,
  CheckCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';

export default function MyDashboardsPage() {
  const { projectId } = useParams();
  const { currentProject, segmentTypes, segments } = useProject();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, [projectId]);

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Dashboards
        </h1>
        <p className="text-slate-600 mt-1">View insights and analytics for {currentProject?.name}</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="dashboard-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">My Progress</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">75%</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <Progress value={75} className="mt-4 h-2" />
            <p className="text-xs text-slate-500 mt-2">15 of 20 questions answered</p>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">People Identified</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">12</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4">Across {segmentTypes.length} segment types</p>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Project Status</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">Active</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4">Survey closes in 14 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Segment Breakdown */}
      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Segment Overview
          </CardTitle>
          <CardDescription>
            Your involvement across different segments
          </CardDescription>
        </CardHeader>
        <CardContent>
          {segmentTypes.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No segments defined for this project yet
            </div>
          ) : (
            <div className="space-y-4">
              {segmentTypes.map((segType) => {
                const typeSegments = segments.filter(s => s.segment_type_id === segType.id);
                return (
                  <div key={segType.id} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-slate-900">{segType.name}</h4>
                      <Badge variant="secondary">{typeSegments.length} options</Badge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {typeSegments.map((seg) => (
                        <Badge key={seg.id} variant="outline" className="text-xs">
                          {seg.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coming Soon */}
      <Card className="dashboard-card border-dashed">
        <CardContent className="py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">More Insights Coming Soon</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Additional dashboards and visualizations will be available as more responses are collected.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
